import {
  usuarioFormSchema,
  type ConviteEmitido,
  type PapelSetorValor,
  type UsuarioForm,
  type UsuarioResumo,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Plus,
  ShieldOff,
  Trash2,
  Upload,
} from 'lucide-react';
import { useState } from 'react';
import { ImportarPlanilha } from '../gestao/ImportarPlanilha';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';

const FORM_VAZIO: UsuarioForm = {
  nome: '',
  email: '',
  perfilId: '',
  limiteSimultaneo: 5,
  ativo: true,
  setores: [],
};

export function Usuarios() {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<{ id: string | null; form: UsuarioForm } | null>(null);
  const [convite, setConvite] = useState<ConviteEmitido | null>(null);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const usuarios = useQuery({
    queryKey: ['usuarios', busca],
    queryFn: () => gestaoApi.listarUsuarios(busca || undefined),
  });
  const perfis = useQuery({ queryKey: ['perfis'], queryFn: gestaoApi.listarPerfis });
  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });

  const recarregar = () => void queryClient.invalidateQueries({ queryKey: ['usuarios'] });
  const tratarErro = (e: unknown) =>
    setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível concluir');

  const salvar = useMutation<
    ConviteEmitido | UsuarioResumo,
    unknown,
    { id: string | null; form: UsuarioForm }
  >({
    mutationFn: ({ id, form }) =>
      id ? gestaoApi.atualizarUsuario(id, form) : gestaoApi.criarUsuario(form),
    onSuccess: (resultado) => {
      recarregar();
      setEditando(null);
      setErro(null);
      if (resultado && 'link' in resultado) setConvite(resultado);
    },
    onError: tratarErro,
  });

  const reenviar = useMutation({
    mutationFn: (id: string) => gestaoApi.reenviarConvite(id),
    onSuccess: (c) => {
      // O "enviando…" sai de cena: a janela com o link é o resultado.
      setAviso(null);
      setConvite(c);
      recarregar();
    },
    onError: (e) => {
      setAviso(null);
      tratarErro(e);
    },
  });

  const redefinirTotp = useMutation({
    mutationFn: (id: string) => gestaoApi.redefinirTotp(id),
    onSuccess: (u) => {
      setAviso(
        `Autenticador de ${u.nome} redefinido. Ele vai configurar de novo no próximo acesso.`,
      );
      recarregar();
    },
    onError: tratarErro,
  });

  const excluir = useMutation({
    mutationFn: (id: string) => gestaoApi.excluirUsuario(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  function abrirEdicao(u: UsuarioResumo) {
    setEditando({
      id: u.id,
      form: {
        nome: u.nome,
        email: u.email,
        perfilId: u.perfil.id,
        limiteSimultaneo: u.limiteSimultaneo,
        ativo: u.ativo,
        setores: u.setores.map((s) => ({
          setorId: s.setorId,
          papel: s.papel,
          ordem: s.ordem,
        })),
      },
    });
    setErro(null);
  }

  function enviar() {
    if (!editando) return;
    const parse = usuarioFormSchema.safeParse(editando.form);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    salvar.mutate({ id: editando.id, form: parse.data });
  }

  function alternarSetor(setorId: string, papel: PapelSetorValor | null) {
    if (!editando) return;
    const atual = editando.form.setores.find((s) => s.setorId === setorId);
    const outros = editando.form.setores.filter((s) => s.setorId !== setorId);
    // O primeiro setor marcado vira o principal; os seguintes entram como apoio. Depois
    // disso o admin muda no seletor, se quiser.
    const ordem = atual?.ordem ?? (outros.length === 0 ? 0 : 1);
    setEditando({
      ...editando,
      form: { ...editando.form, setores: papel ? [...outros, { setorId, papel, ordem }] : outros },
    });
  }

  function mudarOrdem(setorId: string, ordem: number) {
    if (!editando) return;
    setEditando({
      ...editando,
      form: {
        ...editando.form,
        setores: editando.form.setores.map((s) => (s.setorId === setorId ? { ...s, ordem } : s)),
      },
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Usuários</h1>
          <p className="text-sm text-neutral-500">
            Quem acessa o painel. Um a um, a senha é definida pelo próprio usuário, por link; na
            importação por planilha ela vem provisória e é trocada no primeiro acesso.
          </p>
        </div>
        <div className="flex gap-2">
          <Botao variante="secundario" onClick={() => setImportando(true)}>
            <Upload size={16} /> Importar planilha
          </Botao>
          <Botao
            onClick={() => {
              setEditando({
                id: null,
                form: { ...FORM_VAZIO, perfilId: perfis.data?.[0]?.id ?? '' },
              });
              setErro(null);
            }}
          >
            <Plus size={16} /> Novo usuário
          </Botao>
        </div>
      </header>

      {importando && (
        <ImportarPlanilha
          tipo="usuarios"
          setores={setores.data ?? []}
          perfis={perfis.data ?? []}
          aoFechar={() => setImportando(false)}
          aoImportar={recarregar}
        />
      )}

      {erro && !editando && <Aviso tipo="erro">{erro}</Aviso>}
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}

      <CampoTexto
        rotulo="Buscar"
        placeholder="Nome ou e-mail"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        className="max-w-xs"
      />

      <div className="overflow-x-auto rounded-card border border-neutral-200 dark:border-neutral-800">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs text-neutral-500 dark:bg-neutral-900">
            <tr>
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">Perfil</th>
              <th className="px-4 py-2 font-medium">Setores</th>
              <th className="px-4 py-2 font-medium">Situação</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {usuarios.data?.map((u) => (
              <tr key={u.id} className="border-t border-neutral-200 dark:border-neutral-800">
                <td className="px-4 py-3">
                  <p className={u.ativo ? 'font-medium' : 'font-medium text-neutral-400'}>
                    {u.nome}
                  </p>
                  <p className="text-xs text-neutral-500">{u.email}</p>
                </td>
                <td className="px-4 py-3">{u.perfil.nome}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">
                  {u.setores.length === 0
                    ? '—'
                    : u.setores
                        .map((s) => `${s.nome}${s.papel === 'supervisor' ? ' (sup.)' : ''}`)
                        .join(', ')}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5 text-xs">
                    {!u.ativo && <span className="text-neutral-500">inativo</span>}
                    {u.ativo && u.aguardandoPrimeiroAcesso && (
                      <span className="text-amber-700 dark:text-amber-400">
                        {/* Quem veio de planilha tem senha para receber, não link para clicar. */}
                        {u.senhaTemporaria ? 'senha provisória' : 'aguardando 1º acesso'}
                      </span>
                    )}
                    {u.ativo && !u.aguardandoPrimeiroAcesso && (
                      <span className="text-green-700 dark:text-green-400">ativo</span>
                    )}
                    {u.bloqueadoAte && (
                      <span className="text-red-600">bloqueado por tentativas</span>
                    )}
                    <span className="text-neutral-500">
                      2FA {u.totpAtivo ? 'ativo' : 'não configurado'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1">
                    <IconeAcao rotulo="Editar" onClick={() => abrirEdicao(u)}>
                      <Pencil size={15} />
                    </IconeAcao>
                    {/* Enviar o e-mail leva alguns segundos (é o servidor de e-mail do
                        outro lado). Sem o botão travado e sem o relógio girando, parecia
                        que o clique não tinha funcionado — e o admin clicava de novo, o que
                        enchia a caixa da pessoa de links e invalidava o anterior a cada
                        envio: só o último funciona (relato de 30/09). */}
                    <IconeAcao
                      rotulo="Enviar link de senha"
                      ocupado={reenviar.isPending && reenviar.variables === u.id}
                      onClick={() => {
                        if (reenviar.isPending) return;
                        setAviso(`Enviando o link para ${u.email}…`);
                        reenviar.mutate(u.id);
                      }}
                    >
                      {reenviar.isPending && reenviar.variables === u.id ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Mail size={15} />
                      )}
                    </IconeAcao>
                    {u.totpAtivo && (
                      <IconeAcao
                        rotulo="Redefinir autenticador"
                        onClick={() => {
                          if (confirm(`Redefinir o autenticador de ${u.nome}?`))
                            redefinirTotp.mutate(u.id);
                        }}
                      >
                        <ShieldOff size={15} />
                      </IconeAcao>
                    )}
                    {u.podeExcluir && (
                      <IconeAcao
                        rotulo="Excluir"
                        perigo
                        onClick={() => {
                          if (confirm(`Excluir ${u.nome}? Esta ação não pode ser desfeita.`)) {
                            excluir.mutate(u.id);
                          }
                        }}
                      >
                        <Trash2 size={15} />
                      </IconeAcao>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {usuarios.data?.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-neutral-500">
            Nenhum usuário encontrado.
          </p>
        )}
      </div>

      {/* Formulário */}
      <Modal
        aberto={!!editando}
        titulo={editando?.id ? 'Editar usuário' : 'Novo usuário'}
        descricao={
          editando?.id
            ? undefined
            : 'Ao salvar, geramos um link para o usuário criar a própria senha.'
        }
        largura="larga"
        onFechar={() => setEditando(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
            <Botao onClick={enviar} carregando={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        {editando && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
            noValidate
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto
                rotulo="Nome completo"
                autoFocus
                value={editando.form.nome}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, nome: e.target.value } })
                }
              />
              <CampoTexto
                rotulo="E-mail"
                type="email"
                value={editando.form.email}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, email: e.target.value } })
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium">
                Perfil
                <select
                  value={editando.form.perfilId}
                  onChange={(e) =>
                    setEditando({
                      ...editando,
                      form: { ...editando.form, perfilId: e.target.value },
                    })
                  }
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <option value="">Selecione…</option>
                  {perfis.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </label>
              <CampoTexto
                rotulo="Conversas simultâneas"
                type="number"
                min={1}
                max={50}
                value={editando.form.limiteSimultaneo}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, limiteSimultaneo: Number(e.target.value) },
                  })
                }
              />
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Setores</legend>
              {setores.data?.length === 0 && (
                <p className="text-sm text-neutral-500">Cadastre um setor primeiro.</p>
              )}
              {setores.data?.map((s) => {
                const vinculo = editando.form.setores.find((v) => v.setorId === s.id);
                return (
                  <div key={s.id} className="flex items-center gap-3 text-sm">
                    <label className="flex flex-1 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!vinculo}
                        onChange={(e) => alternarSetor(s.id, e.target.checked ? 'atendente' : null)}
                      />
                      {s.nome}
                    </label>
                    {vinculo && (
                      <>
                        {/* Principal x apoio: a fila de um setor entrega primeiro a quem o
                            tem como principal, e só cai no apoio quando não sobrar
                            ninguém. É o que impede o setor principal de ficar descoberto
                            porque a pessoa foi puxada para cobrir outro. */}
                        <select
                          value={vinculo.ordem}
                          aria-label={`Prioridade em ${s.nome}`}
                          onChange={(e) => mudarOrdem(s.id, Number(e.target.value))}
                          className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                        >
                          <option value={0}>Principal</option>
                          <option value={1}>Apoio</option>
                          <option value={2}>Apoio 2</option>
                        </select>
                        <select
                          value={vinculo.papel}
                          aria-label={`Papel em ${s.nome}`}
                          onChange={(e) => alternarSetor(s.id, e.target.value as PapelSetorValor)}
                          className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                        >
                          <option value="atendente">Atendente</option>
                          <option value="supervisor">Supervisor</option>
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
            </fieldset>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editando.form.ativo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, ativo: e.target.checked } })
                }
              />
              Usuário ativo (desmarcar encerra as sessões dele)
            </label>

            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </form>
        )}
      </Modal>

      {/* Link do convite */}
      <Modal
        aberto={!!convite}
        titulo="Link de acesso gerado"
        onFechar={() => setConvite(null)}
        acoes={<Botao onClick={() => setConvite(null)}>Fechar</Botao>}
      >
        {convite && (
          <div className="space-y-3">
            <Aviso tipo={convite.enviadoPorEmail ? 'sucesso' : 'atencao'}>
              {convite.enviadoPorEmail
                ? `Enviamos o link para ${convite.usuario.email}.`
                : 'O envio por e-mail ainda não está configurado. Copie o link e repasse por um canal seguro.'}
            </Aviso>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {convite.usuario.nome} vai definir a própria senha. O link vale até{' '}
              {new Date(convite.expiraEm).toLocaleString('pt-BR')} e só pode ser usado uma vez.
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={convite.link}
                aria-label="Link de acesso"
                onFocus={(e) => e.target.select()}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 font-mono text-xs dark:border-neutral-700 dark:bg-neutral-900"
              />
              <Botao
                variante="secundario"
                onClick={() => {
                  void navigator.clipboard.writeText(convite.link);
                  setAviso('Link copiado.');
                }}
              >
                <Copy size={14} /> Copiar
              </Botao>
            </div>
            <p className="flex items-center gap-1 text-xs text-neutral-500">
              <KeyRound size={12} /> Nunca envie senhas por mensagem: o link permite que a pessoa
              crie a dela.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function IconeAcao({
  rotulo,
  onClick,
  perigo,
  ocupado,
  children,
}: {
  rotulo: string;
  onClick: () => void;
  perigo?: boolean;
  /** Em andamento: o botão trava e o cursor muda, para ninguém clicar duas vezes. */
  ocupado?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={ocupado ? `${rotulo}…` : rotulo}
      aria-label={rotulo}
      aria-busy={ocupado}
      disabled={ocupado}
      onClick={onClick}
      className={`rounded p-1.5 transition hover:bg-neutral-100 disabled:cursor-wait disabled:opacity-50 dark:hover:bg-neutral-800 ${
        perigo ? 'text-red-600' : 'text-neutral-500'
      }`}
    >
      {children}
    </button>
  );
}
